// App.js
import '../src/scss/app.scss'
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import FooterComponent from './components/Footer/FooterComponent';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react';
import { isUserLoggedIn } from './actions/auth.action';
import { updateCart, updateDesires } from './actions';
import { subscribeUserNotifications, resetAndLoadFirstPage, loadNotificationCounts } from './actions/notificationActions';
import { updateGalleryImgUserItem } from './actions/galleryImgUser';
import MainPage from './mainPage/MainPage';
import ContainerPage from './sites/ContainerPage';
import OfflinePage from './OfflinePage';
import ScrollBlock from './components/ScrollBlock';
import AccessGateHost from './components/Billing/AccessGateHost';
import FloatingChatHost from './components/FloatingChat/FloatingChatHost';
import NotificationToastHost from './components/Notifications/NotificationToastHost';
import SceneGuestEntryPage from './containers/SceneGuestAccess/SceneGuestEntryPage';
import SceneGuestViewerPage from './containers/SceneGuestAccess/SceneGuestViewerPage';
import { BlankMaterialCatalogProvider } from './contexts/BlankMaterialCatalogContext';
import GlobalOfficeOrderModal from './containers/AdminOffice/components/GlobalOfficeOrderModal';
import GlobalOfficeTaskCreateModal from './containers/AdminOffice/components/GlobalOfficeTaskCreateModal';
import GlobalOfficeEntityModal from './containers/AdminOffice/components/GlobalOfficeEntityModal';
import GlobalProductGalleryViewer from './containers/AdminOffice/components/GlobalProductGalleryViewer';
import GlobalOfficeProduct3DViewer from './containers/AdminOffice/components/GlobalOfficeProduct3DViewer';
import SystemPresenceBridge from './components/SystemPresence/SystemPresenceBridge';

const HomePage = lazy(() => import('./containers/HomePage/HomePage'));
const Catalog = lazy(() => import('./containers/Catalog'));
const Contact = lazy(() => import('./containers/Contact/Contact'));
const AboutUs = lazy(() => import('./containers/AboutUs/AboutUs'));
const Products = lazy(() => import('./containers/Products/Products'));
const PortfolioPage = lazy(() => import('./containers/PortfolioPage/PortfolioPage'));
const Architect = lazy(() => import('./containers/Architect/Architect'));
const VideoPortfolioPage = lazy(() => import('./containers/VideoPortfolioPage/VideoPortfolioPage'));
const BlogPage = lazy(() => import('./containers/Blog/BlogPage'));
const BlogDetailsPage = lazy(() => import('./containers/Blog/BlogDetailsPage'));
const ProductDetails = lazy(() => import('./containers/Products/ProductDetails/ProductDetails'));
const PortfolioPageDetails = lazy(() => import('./containers/PortfolioPage/PortfolioPageDetails/PortfolioPageDetails'));
const EmailNewsletterPage = lazy(() => import('./containers/EmailNewsletterPage'));
const CheckoutPage = lazy(() => import('./containers/Chekout/CheckoutPage'));
const OrderPage = lazy(() => import('./containers/CabinetPage/OrderPage/OrderPage'));
const PersonalInformation = lazy(() => import('./containers/CabinetPage/PersonalInfo/PersonalInformation'));
const HistoryProductPage = lazy(() => import('./containers/CabinetPage/HistoryPage/HistoryProductPage'));
const DesiresPage = lazy(() => import('./containers/CabinetPage/DesiresPage/DesiresPage'));
const MailingList = lazy(() => import('./containers/CabinetPage/MailingListPage/MailingList'));
const ApplicationPage = lazy(() => import('./containers/CabinetPage/ApplicationPage/ApplicationPage'));
const PartnerPage = lazy(() => import('./containers/CabinetPage/PartnerPage/PartnerPage'));
const GalleryPage = lazy(() => import('./containers/Gallery/GalleryPage'));
const ElementPaymentPage = lazy(() => import('./containers/ElementPaymentPage/ElementPaymentPage'));
const ContactM = lazy(() => import('./containers/ContactM/ContactM'));
const SettingPage = lazy(() => import('./containers/CabinetPage/SettingPage/SettingPage'));
const PartnerPageSale = lazy(() => import('./containers/PartnerPageSale/PartnerPageSale'));
const FencePageContainer = lazy(() => import('./containers/FenceModel/FencePageContainer'));
const ChatUI = lazy(() => import('./containers/ChatUI/ChatUI'));
const ChatEntryPage = lazy(() => import('./containers/ChatUI/ChatEntryPage'));
const OrderPayment = lazy(() => import('./containers/PaymentOrder/OrderPayment'));
const AdminLayout = lazy(() => import('./containers/AdminOffice/AdminLayout'));
const OfficeInviteAccept = lazy(() => import('./containers/AdminOffice/OfficeSettings/OfficeInviteAccept'));
const MySceneProjectsPage = lazy(() => import('./containers/SceneGuestAccess/MySceneProjectsPage'));
const CustomerSalesSessionPageAO = lazy(() => import('./containers/AdminOffice/SalesSessions/CustomerSalesSessionPageAO'));

function App() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth, shallowEqual);
  const [isOnline, setIsOnline] = useState(true);
  const [showFooter, setShowFooter] = useState(true);

  useEffect(() => {
    function handleOnlineStatus() {
      setIsOnline(true);
    }

    function handleOfflineStatus() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
    }
  }, []);

  useEffect(() => {
    dispatch(isUserLoggedIn());
    dispatch(updateCart());
    dispatch(updateDesires());
    dispatch(updateGalleryImgUserItem());
  }, [dispatch]);

  useEffect(() => {
    if (!auth.authenticate || !auth.user?._id) return undefined;

    const unsubscribe = dispatch(subscribeUserNotifications());

    dispatch(resetAndLoadFirstPage({
      scope: 'personal',
      officeId: null,
    }));
    dispatch(loadNotificationCounts());

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [auth.authenticate, auth.user?._id, dispatch]);

  const memoizedRoutes = useMemo(() => (
    <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', background: '#000', color: '#fff' }}>Загрузка…</div>}>
      <Routes>
        <Route path="/scene-access" element={<SceneGuestEntryPage setShowFooter={setShowFooter} />} />
        <Route path="/customer-session/:officeId/:sessionId" element={<CustomerSalesSessionPageAO setShowFooter={setShowFooter} />} />
        <Route path="/scene-access/project/:grantId" element={<SceneGuestViewerPage setShowFooter={setShowFooter} savedProject />} />
        <Route path="/scene-access/:accessKey" element={<SceneGuestViewerPage setShowFooter={setShowFooter} />} />
        <Route path="/mySceneProjects" element={<MySceneProjectsPage />} />
        <Route path="/office-invite/:token" element={<OfficeInviteAccept />} />
        <Route path="/adminLayout" element={<AdminLayout setShowFooter={setShowFooter} />} />
        <Route path="/payment/success" element={<OrderPayment />} />
        <Route path="/chat/:chatId" element={<ChatUI />} />
        <Route path="/chat" element={<ChatEntryPage />} />
        <Route path="/maf" element={<HomePage />} />
        <Route path="/productDetails/:productId" element={<ProductDetails />} />
        <Route path="/blogDetails/:blogId" element={<BlogDetailsPage />} />
        <Route path="/portfolioPage" element={<PortfolioPage />} />
        <Route path="/videoPortfolioPage" element={<VideoPortfolioPage />} />
        <Route path="/productsCategory/:slug" element={<Products />} />
        <Route path="/fenceCalculator" element={<FencePageContainer setShowFooter={setShowFooter} />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/contacts" element={<Contact />} />
        <Route path="/aboutUs" element={<AboutUs />} />
        <Route path="/architect" element={<Architect />} />
        <Route path="/portfolioDetails/:portfolioId" element={<PortfolioPageDetails />} />
        <Route path="/emailNewsletter" element={<EmailNewsletterPage />} />
        <Route path="/checkoutPage" element={<CheckoutPage setShowFooter={setShowFooter} />} />
        <Route path="/historyProductPage" element={<HistoryProductPage />} />
        <Route path="/orderPage" element={<OrderPage />} />
        <Route path="/desiresPage" element={<DesiresPage />} />
        <Route path="/personalInformation" element={<PersonalInformation />} />
        <Route path="/partnerPage" element={<PartnerPage />} />
        <Route path="/partnerPageSale" element={<PartnerPageSale setShowFooter={setShowFooter} />} />
        <Route path="/mailingList" element={<MailingList />} />
        <Route path="/user/settingPage" element={<SettingPage />} />
        <Route path="/applicationPage" element={<ApplicationPage />} />
        <Route path="/applicationCallMePage" element={<ApplicationPage callMe />} />
        <Route path="/galleryPage" element={<GalleryPage />} />
        <Route path="/elementPaymentPage" element={<ElementPaymentPage />} />
        <Route path="/contactM" element={<ContactM />} />
        <Route path="/" element={<MainPage setShowFooter={setShowFooter} />} />
        <Route path="/mainPage/:mainPageId" element={<ContainerPage setShowFooter={setShowFooter} />} />
      </Routes>
    </Suspense>
  ), []);

  return (
    <>
      {isOnline ? (
        <>
          <BlankMaterialCatalogProvider>
            <BrowserRouter>
              <SystemPresenceBridge />
              {memoizedRoutes}
              <FloatingChatHost />
              <GlobalOfficeOrderModal />
              <GlobalOfficeTaskCreateModal />
              <GlobalOfficeEntityModal />
              <GlobalProductGalleryViewer />
              <GlobalOfficeProduct3DViewer />
              <NotificationToastHost showOfficeTabs />
              <AccessGateHost />
              {showFooter && <FooterComponent auth={auth} />}
            </BrowserRouter>
          </BlankMaterialCatalogProvider>
          <ScrollBlock />
        </>
      ) : (
        <OfflinePage />
      )}
    </>
  );
}

export default React.memo(App);
